/**
 * GSAP 全局动画类名说明文档
 * 
 * 本文件定义了一系列通用的 GSAP 动画，通过给 HTML 元素添加对应的 `ga-` 前缀类名即可触发。
 * 
 * --- 基础进入动画 ---
 * 1. .ga-fade-up           : 元素向上淡入。
 * 2. .ga-slide-from-left   : 元素从左侧滑入。
 * 3. .ga-slide-from-right  : 元素从右侧滑入。
 * 
 * --- 文本增强动画 ---
 * 4. .ga-line-reveal-text  : 文字逐行从下往上遮罩出现（推荐用于大标题）。
 * 5. .ga-text-reveal       : 文字按字符/单词交错显示。
 * 6. .ga-highlight         : 文本高亮记号笔涂抹效果。
 * 
 * --- 容器与组合动画 ---
 * 7. .ga-stagger-grid      : 网格容器，其子元素 .ga-card 会依次交错弹出。
 * 8. .ga-image-zoom-container : 容器内图片进入视口时从放大状态恢复原始比例。
 * 9. .ga-svg-container      : SVG 路径自动描边动画。
 * 
 * --- 滚动交互动画 ---
 * 10. .ga-rotate-on-scroll  : 元素随滚动条进度实时旋转。
 * 11. .ga-horizontal-scroll-wrapper : 钉柱横向滚动容器，子元素需带 .ga-panel 类名。
 */

document.addEventListener('DOMContentLoaded', (event) => {
    // 注册插件
    gsap.registerPlugin(DrawSVGPlugin, ScrollTrigger, SplitText);

    // ==========================================
    // 1. 基础进入动画 (Basic Enter Animations)
    // ==========================================

    // 向上平滑淡入 (Fade Up)
    gsap.utils.toArray('.ga-fade-up').forEach((elem) => {
        gsap.from(elem, {
            scrollTrigger: {
                trigger: elem,
                start: "top 80%",
            },
            y: 50,
            opacity: 0,
            duration: 1,
            ease: "power2.out"
        });
    });

    // 左侧滑入 (Slide From Left)
    gsap.utils.toArray('.ga-slide-from-left').forEach((elem) => {
        gsap.from(elem, {
            scrollTrigger: {
                trigger: elem,
                start: "top 80%",
            },
            x: -100,
            opacity: 0,
            duration: 1,
            ease: "power2.out"
        });
    });

    // 右侧滑入 (Slide From Right)
    gsap.utils.toArray('.ga-slide-from-right').forEach((elem) => {
        gsap.from(elem, {
            scrollTrigger: {
                trigger: elem,
                start: "top 80%",
            },
            x: 100,
            opacity: 0,
            duration: 1,
            ease: "power2.out"
        });
    });

    // ==========================================
    // 2. 文本增强动画 (Text Enhancements)
    // ==========================================

    // 待字体加载完成后再初始化 SplitText 动画
    document.fonts.ready.then(() => {
        // 文字逐行从下往上遮罩出现 (Line Reveal)
        gsap.utils.toArray('.ga-line-reveal-text').forEach((textElem) => {
            const childSplit = new SplitText(textElem, {
                type: "lines",
                linesClass: "split-child"
            });
            const parentSplit = new SplitText(textElem, {
                type: "lines",
                linesClass: "split-parent"
            });

            gsap.set(parentSplit.lines, { overflow: "hidden" });

            gsap.from(childSplit.lines, {
                scrollTrigger: {
                    trigger: textElem,
                    start: "top 85%",
                },
                yPercent: 100,
                opacity: 0,
                duration: 1,
                stagger: 0.15,
                ease: "power4.out"
            });
        });

        // 文字按字符交错显示 (SplitText Reveal)
        gsap.utils.toArray('.ga-text-reveal').forEach((textElem) => {
            const split = new SplitText(textElem, { type: "lines, words, chars" });

            gsap.from(split.chars, {
                scrollTrigger: {
                    trigger: textElem,
                    start: "top 85%",
                },
                opacity: 0,
                y: 20,
                rotationX: -90,
                transformOrigin: "0% 50% -50",
                duration: 0.8,
                stagger: 0.02,
                ease: "back.out(1.5)"
            });
        });

        // 文本高亮涂抹效果 (Text Highlight Marker)
        gsap.utils.toArray('.ga-highlight').forEach((highlight) => {
            gsap.fromTo(highlight,
                { backgroundSize: "0% 100%" },
                {
                    scrollTrigger: {
                        trigger: highlight,
                        start: "top 85%",
                    },
                    backgroundSize: "100% 100%",
                    duration: 0.8,
                    ease: "power2.inOut"
                }
            );
        });
    });

    // ==========================================
    // 3. 容器与组合动画 (Container & Group Animations)
    // ==========================================

    // 群组卡片交错弹出 (Staggered Grid)
    gsap.utils.toArray('.ga-stagger-grid').forEach((grid) => {
        const cards = grid.querySelectorAll('.ga-card');

        gsap.from(cards, {
            scrollTrigger: {
                trigger: grid,
                start: "top 80%",
            },
            y: 40,
            scale: 0.9,
            opacity: 0,
            duration: 0.6,
            stagger: 0.15,
            ease: "power3.out"
        });
    });

    // 图片缩放显示 (Image Zoom Reveal)
    gsap.utils.toArray('.ga-image-zoom-container').forEach((container) => {
        const img = container.querySelector('img');
        if (!img) return;

        gsap.from(img, {
            scrollTrigger: {
                trigger: container,
                start: "top 90%",
            },
            scale: 1.3,
            transformOrigin: "center center",
            duration: 1.5,
            ease: "power3.out"
        });
    });

    // SVG 路径描边动画 (SVG Drawing)
    gsap.utils.toArray('.ga-svg-container').forEach((svgContainer) => {
        const paths = svgContainer.querySelectorAll('path, line, polyline');

        gsap.from(paths, {
            scrollTrigger: {
                trigger: svgContainer,
                start: "top 75%",
            },
            drawSVG: "0%",
            duration: 2,
            stagger: 0.2,
            ease: "power2.inOut"
        });
    });

    // ==========================================
    // 4. 滚动交互动画 (Scroll Interaction Animations)
    // ==========================================

    // 随滚动旋转 (Rotate On Scroll)
    gsap.utils.toArray('.ga-rotate-on-scroll').forEach((gear) => {
        gsap.to(gear, {
            scrollTrigger: {
                trigger: gear,
                start: "top bottom",
                end: "bottom top",
                scrub: 1,
            },
            rotation: 360,
            ease: "none"
        });
    });

    // 固定横向滚动 (Pinned Horizontal Scroll)
    const horizontalSection = document.querySelector('.ga-horizontal-scroll-wrapper');
    if (horizontalSection) {
        const panels = gsap.utils.toArray('.ga-horizontal-scroll-wrapper .ga-panel');

        gsap.to(panels, {
            xPercent: -100 * (panels.length - 1),
            ease: "none",
            scrollTrigger: {
                trigger: horizontalSection,
                pin: true,
                scrub: 1,
                snap: 1 / (panels.length - 1),
                end: () => "+=" + horizontalSection.offsetWidth
            }
        });
    }

});